import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { scrollToTarget } from "../lib/smooth";

const HERO_IMG =
  "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1400&q=80";

const EASE = [0.215, 0.61, 0.355, 1];

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: EASE, delay: 0.25 + i * 0.16 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  const onMouseMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 12);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 12);
  };

  return (
    <section ref={ref} onMouseMove={onMouseMove} className="relative overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-6xl px-6 pt-32 md:pt-44 pb-16 md:pb-24 grid md:grid-cols-12 gap-14 md:gap-10 items-center">
        <motion.div style={{ opacity: fade }} className="md:col-span-7 md:-ml-4 xl:-ml-12">
          <h1
            className="font-serif tracking-tight mt-8 whitespace-nowrap leading-none"
            style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.25rem)" }}
          >
            <span className="block overflow-hidden pb-2">
              <motion.span className="block" variants={lineAnim} custom={0} initial="hidden" animate="show">
                Les Mots pour le <span className="italic text-vermilion">Dire</span>
              </motion.span>
            </span>
          </h1>

          <motion.blockquote
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="font-serif italic text-xl sm:text-2xl text-stone-700 mt-9 max-w-xl leading-snug"
          >
            « Il vous manque le temps, une plume ou simplement les mots pour le dire ? »
          </motion.blockquote>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.88, ease: EASE }}
            className="text-soot leading-relaxed mt-5 max-w-lg text-base md:text-lg"
          >
            Je vous accompagne dans la rédaction, la correction et la réécriture de vos écrits.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-11 flex flex-wrap items-center gap-4"
          >
            <button type="button" onClick={() => scrollToTarget("#contact")} data-testid="hero-cta-contact" className="btn-primary">
              Me contacter
            </button>
            <button type="button" onClick={() => scrollToTarget("#services")} data-testid="hero-cta-prestations" className="btn-ghost">
              Découvrir les prestations
            </button>
          </motion.div>
        </motion.div>

        <div className="md:col-span-5 relative">
          <motion.div style={{ x: sx, y: sy }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
              className="overflow-hidden border border-hairline"
            >
              <motion.img
                style={{ y: imgY, scale: 1.16 }}
                src={HERO_IMG}
                alt="Stylo plume en train d'écrire sur une feuille de papier ligné"
                className="w-full h-[420px] md:h-[560px] object-cover img-editorial"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
